/**
 * utils/api.js
 * Communications avec le backend FastAPI (REST + streaming SSE).
 */

import { getAccessToken } from "./auth";

const BACKEND_BASE_URL = process.env.REACT_APP_API_URL || "https://api.acomail.aco.pro";

/**
 * Construit les en-têtes d'identité.
 * En prod, un access token Entra signé est envoyé via Authorization: Bearer.
 * En dev local (auth non configurée), on retombe sur l'en-tête X-User-Email.
 *
 * @param {boolean} forceRefresh  Force le renouvellement du jeton (réessai après 401).
 */
async function authHeaders(userEmail, forceRefresh = false) {
  const token = await getAccessToken({ forceRefresh });
  if (token) return { Authorization: `Bearer ${token}` };
  return { "X-User-Email": userEmail };
}

/**
 * Exécute une requête fetch authentifiée.
 * Si le backend répond 401 (jeton périmé/invalide),
 * on renouvelle le jeton de force et on réessaie une seule fois.
 */
async function authedFetch(url, init, userEmail) {
  let response = await fetch(url, {
    ...init,
    headers: { ...init.headers, ...(await authHeaders(userEmail)) },
  });

  if (response.status === 401) {
    response = await fetch(url, {
      ...init,
      headers: { ...init.headers, ...(await authHeaders(userEmail, true)) },
    });
  }
  return response;
}

/**
 * POST a payload and consume a Server-Sent Events token stream.
 *
 * Calls `onChunk(delta, accumulated)` for every token piece as it arrives and
 * resolves with the full accumulated text once the stream completes.
 * Passing a `signal` lets the caller abort mid-stream,
 * aborting closes the connection, which the backend detects to stop LLM generation.
 */
async function streamPost({ endpoint, payload, userEmail, onChunk, signal }) {
  const response = await authedFetch(
    `${BACKEND_BASE_URL}/${endpoint}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(payload),
      signal,
    },
    userEmail
  );

  if (!response.ok) {
    const err = new Error(`Backend error: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  if (!response.body) {
    throw new Error("Streaming not supported by this browser.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulated = "";

  // Each SSE event is delimited by a blank line ("\n\n").
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIdx;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);

      for (const line of rawEvent.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data || data === "[DONE]") continue;
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            throw new Error(parsed.error);
          }
          if (typeof parsed.content === "string") {
            accumulated += parsed.content;
            onChunk?.(parsed.content, accumulated);
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
  }

  return accumulated;
}

export async function summarize({
  subject,
  body,
  language,
  tone,
  length,
  userEmail,
  signal,
  onChunk,
}) {
  return streamPost({
    endpoint: "email/summarize",
    payload: { subject, body, language, tone, length },
    userEmail,
    signal,
    onChunk,
  });
}

export async function extractNER({
  subject,
  body,
  language,
  tone,
  length,
  userEmail,
  signal,
  onChunk,
}) {
  return streamPost({
    endpoint: "email/ner",
    payload: { subject, body, language, tone, length },
    userEmail,
    signal,
    onChunk,
  });
}

export async function translate({ subject, body, targetLanguage, userEmail, signal, onChunk }) {
  return streamPost({
    endpoint: "email/translate",
    payload: { subject, body, target_language: targetLanguage },
    userEmail,
    signal,
    onChunk,
  });
}

export async function streamChatDraft({ payload, userEmail, onChunk, signal }) {
  return streamPost({ endpoint: "email/chat", payload, userEmail, onChunk, signal });
}
